import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { RyeMainContentCard } from "../rye/RyeMainContentCard";
import { buildClasses } from "../utils/buildClasses";
import { RyeScreenWrapper } from "../rye/RyeScreenWrapper";
import { RyeMainContentHeader } from "../rye/RyeMainContentHeader";
import { RyeMainContentSpacer } from "../rye/RyeMainContentSpacer";
import { TransactionsTable } from "./TransactionsTable";
import { TransactionsColumnHeaders } from "./TransactionsColumnHeaders";
import {
  TransactionsAmountWidthContext,
  useTransactionsAmountWidth,
} from "./transactionsAmountWidth";
import {
  RyeMainContentScrollShadow,
  RyeScrollListener,
  useRyeScrolled,
} from "../rye/RyeMainContentScrollShadow";
import { TransactionColumn } from "./TransactionColumn";
import { RyeButton } from "../rye/RyeButton";
import { RyeBadge } from "../rye/RyeBadge";
import {
  BreadAccount,
  BreadTransaction,
  FirestoreDocCategory,
  FirestoreDocTag,
  P,
  Predicate,
} from "breadcommon";
import { TransactionsSearchBar } from "./TransactionsSearchBar";
import dayjs from "dayjs";
import { TransactionsContext } from "../firebaseio/TransactionsContext";
import { AccountsContext } from "../firebaseio/AccountsContext";
import { CategoriesContext } from "../firebaseio/CategoriesContext";
import { TagsContext } from "../firebaseio/TagsContext";
import { LoadingInitialDataContext } from "../LoadUser";
import { RyeSkeleton } from "../rye/RyeSkeleton";
import { STANDARD_MAIN_CONTENT_CARD_PADDING_X } from "../rye/RyeMainContentContainerBase";
import { TransactionsFooter } from "./TransactionsFooter";
import { TransactionsScreenFilterBadge } from "./TransactionsScreenFilterBadge";
import { TransactionColumnFilterDisplayDate } from "./TransactionColumnFilterDisplayDate";
import { TransactionColumnFilterDisplayAccount } from "./TransactionColumnFilterDisplayAccount";
import { TransactionColumnFilterDisplayType } from "./TransactionColumnFilterDisplayType";
import { TransactionColumnFilterDisplayMerchant } from "./TransactionColumnFilterDisplayMerchant";
import { TransactionColumnFilterDisplayDescription } from "./TransactionColumnFilterDisplayDescription";
import { TransactionColumnFilterDisplayCategory } from "./TransactionColumnFilterDisplayCategory";
import { TransactionColumnFilterDisplayRule } from "./TransactionColumnFilterDisplayRule";
import { TransactionColumnFilterDisplayReviewed } from "./TransactionColumnFilterDisplayReviewed";
import { TransactionColumnFilterDisplayAmount } from "./TransactionColumnFilterDisplayAmount";
import { TransactionColumnFilterDisplayTags } from "./TransactionColumnFilterDisplayTags";
import { BulkEditBar } from "./BulkEditBar";
import { RyeEmptyState } from "../rye/RyeEmptyState";
import { RyeNoResults } from "../rye/RyeNoResults";
import { useRyeModal } from "../rye/RyeModal";
import { AddNewAccountModal } from "../accounts_screen/AddNewAccountModal";
import { useOpenSettings } from "../settings_screen/settingsDefinitions";
import {
  defaultTransactionsNav,
  SortOrders,
  TransactionsNavContext,
} from "./TransactionsNavTypes";
import {
  deserializeTransactionsNav,
  serializeTransactionsNav,
} from "./transactionsNavSerialization";

export {
  SortOrders,
  TransactionsNavContext,
  defaultTransactionsNav,
} from "./TransactionsNavTypes";
export type { ColumnSettings, TransactionsNav } from "./TransactionsNavTypes";

export function TransactionsScreen(): JSX.Element {
  let transactions = useContext(TransactionsContext);
  const accounts = useContext(AccountsContext);
  const categories = useContext(CategoriesContext);
  const tags = useContext(TagsContext);
  const loadingInitialData = useContext(LoadingInitialDataContext);

  const [transactionsNav, setTransactionsNav] = useContext(
    TransactionsNavContext,
  );
  const [selectedTransactionCount, setSelectedTransactionCount] = useState(0);
  const [selectedTransactionIds, setSelectedTransactionIds] = useState<
    Set<string>
  >(new Set());
  const deselectAllRef = useRef<(() => void) | null>(null);
  // Sized to all transactions, not just those shown, so it holds still while
  // searching and filtering.
  const amountWidth = useTransactionsAmountWidth(transactions);
  // The column headers sit in the top bar, above the scrolling table.
  const { scrolled, moreBelow, onScroll: onTableScroll } = useRyeScrolled();
  // The transactions a review started with, kept so it can count how many of
  // them are still unreviewed.
  const [reviewingIds, setReviewingIds] = useState<Set<string> | null>(null);
  const { openModal } = useRyeModal();
  const openSettings = useOpenSettings();
  const [viewingCount, setViewingCount] = useState<number | null>(null);

  const handleSelectedIdsChange = useCallback((ids: Set<string>) => {
    setSelectedTransactionIds(ids);
  }, []);

  // Keep the URL in sync with the current nav state while the transactions
  // screen is mounted. On mount, if the URL has a `nav` param that differs
  // from the current context nav, load the URL value into state (this handles
  // users landing on a shared/bookmarked URL mid-session). Otherwise, write
  // the current nav back to the URL so refreshes and shares work. URL writes
  // use `replace` so they don't pollute browser history — the custom back
  // button in the header has its own history stack.
  const location = useLocation();
  const navigate = useNavigate();
  const didInitUrlSyncRef = useRef(false);
  const defaultNavSerialized = useRef(
    serializeTransactionsNav(defaultTransactionsNav),
  );
  useEffect(() => {
    const currentParam = new URLSearchParams(location.search).get("nav");
    const currentSerialized = serializeTransactionsNav(transactionsNav);

    if (!didInitUrlSyncRef.current) {
      didInitUrlSyncRef.current = true;
      if (currentParam && currentParam !== currentSerialized) {
        const parsed = deserializeTransactionsNav(currentParam);
        if (parsed) {
          setTransactionsNav(parsed);
          return;
        }
      }
    }

    const params = new URLSearchParams(location.search);
    const isDefault = currentSerialized === defaultNavSerialized.current;
    if (isDefault) {
      if (params.has("nav")) {
        params.delete("nav");
        navigate(
          { pathname: location.pathname, search: params.toString() },
          { replace: true },
        );
      }
    } else if (currentParam !== currentSerialized) {
      params.set("nav", currentSerialized);
      navigate(
        { pathname: location.pathname, search: params.toString() },
        { replace: true },
      );
    }
  }, [
    transactionsNav,
    location.pathname,
    location.search,
    navigate,
    setTransactionsNav,
  ]);

  // Helper function to focus the first transaction row
  const focusFirstTransactionRow = () => {
    setTimeout(() => {
      // Find the first transaction row element (row index 0)
      const firstRowElement = document.querySelector(
        '[data-transaction-row="0"]',
      ) as HTMLDivElement;
      if (firstRowElement) {
        firstRowElement.focus();
        firstRowElement.scrollIntoView({
          block: "center",
          inline: "nearest",
          behavior: "smooth",
        });
      }
    }, 150); // Give time for DOM updates and state changes
  };

  if (
    loadingInitialData.transactions ||
    loadingInitialData.categories ||
    loadingInitialData.accounts ||
    loadingInitialData.institutions ||
    loadingInitialData.rules
  ) {
    return (
      <RyeScreenWrapper>
        <RyeMainContentHeader title="Transactions">
          <div className={buildClasses("w-full", "flex", "items-center")}>
            <RyeSkeleton
              className={buildClasses("h-7", "w-72", "rounded-md")}
            />
          </div>
        </RyeMainContentHeader>
        <TransactionsColumnHeaders />
        <div
          className={buildClasses(
            "relative",
            "flex-grow",
            "flex-shrink",
            "flex",
            "flex-col",
            "min-h-0",
          )}
        >
          <RyeMainContentCard
            padTop={false}
            roundedTl={false}
            roundedTr={false}
            roundedBr={false}
            roundedBl={false}
          >
            {null}
          </RyeMainContentCard>
          {/* Over the card rather than in its scrolling body: more rows than
          any screen shows fill the table's height, without a scrollbar. */}
          <div
            role="status"
            aria-label="Loading transactions"
            className={buildClasses(
              "absolute",
              "inset-0",
              "overflow-hidden",
              "pointer-events-none",
              STANDARD_MAIN_CONTENT_CARD_PADDING_X,
            )}
          >
            {Array.from({ length: 40 }, (_, i) => (
              <TransactionSkeletonRow key={i} />
            ))}
          </div>
        </div>
        <RyeMainContentSpacer direction={"h"} />
      </RyeScreenWrapper>
    );
  }

  const hasAnyInvestmentTransactions = Array.from(transactions.values()).some(
    (transaction) => transaction.isInvestmentTransaction,
  );
  if (
    hasAnyInvestmentTransactions !==
    transactionsNav.columnSettings[TransactionColumn.TYPE].shown
  ) {
    setTransactionsNav((prevState) => ({
      ...prevState,
      columnSettings: {
        ...prevState.columnSettings,
        [TransactionColumn.TYPE]: {
          ...prevState.columnSettings[TransactionColumn.TYPE],
          shown: hasAnyInvestmentTransactions,
        },
      },
    }));
  }

  // compute search string predicate and full predicate
  let searchStringPredicate: Predicate | null = null;
  if (transactionsNav.searchString) {
    let relevantCategoryIds: string[] = [];
    let relevantAccountIds: string[] = [];
    if (transactionsNav.searchStringExactMatch) {
      relevantCategoryIds = Array.from(categories.values())
        .filter(
          (category) =>
            category.name.toLowerCase() ===
            transactionsNav.searchString.toLowerCase(),
        )
        .map((category) => category.id);
      relevantAccountIds = Array.from(accounts.values())
        .filter(
          (account) =>
            account.name.toLowerCase() ===
            transactionsNav.searchString.toLowerCase(),
        )
        .map((account) => account.id);
    } else {
      relevantCategoryIds = Array.from(categories.values())
        .filter((category) =>
          category.name
            .toLowerCase()
            .includes(transactionsNav.searchString.toLowerCase()),
        )
        .map((category) => category.id);
      relevantAccountIds = Array.from(accounts.values())
        .filter((account) =>
          account.name
            .toLowerCase()
            .includes(transactionsNav.searchString.toLowerCase()),
        )
        .map((account) => account.id);
    }
    const stringCondition = transactionsNav.searchStringExactMatch
      ? P.EqualsCI
      : P.SubstringCI;
    searchStringPredicate = P.Any(
      P.Any(
        ...relevantCategoryIds.map((category_id) =>
          P.Equals(
            P.Field<BreadTransaction, "categoryId">("categoryId"),
            P.Literal(category_id),
          ),
        ),
      ),
      P.Any(
        ...relevantAccountIds.map((account_id) =>
          P.Equals(
            P.Field<BreadTransaction, "account_id">("account_id"),
            P.Literal(account_id),
          ),
        ),
      ),
      stringCondition(
        P.Field<BreadTransaction, "description">("description"),
        P.Literal(transactionsNav.searchString),
      ),
      stringCondition(
        P.NestedField<BreadTransaction, "merchant", "name">("merchant", "name"),
        P.Literal(transactionsNav.searchString),
      ),
      stringCondition(
        P.Field<BreadTransaction, "type">("type"),
        P.Literal(transactionsNav.searchString),
      ),
      stringCondition(
        P.Field<BreadTransaction, "subtype">("subtype"),
        P.Literal(transactionsNav.searchString),
      ),
    );
  }

  let fullPredicate: P.Predicate | null = null;
  const columnPredicates = Object.values(transactionsNav.columnSettings)
    .map((settings) => settings.predicate)
    .filter((predicate): predicate is Predicate => predicate !== null);
  if (searchStringPredicate != null || columnPredicates.length > 0) {
    fullPredicate = P.All(
      ...columnPredicates,
      searchStringPredicate ?? P.Literal(true),
    );
  }

  const sortedFilteredTransactions = sortTransactions(
    Array.from(transactions.values()).filter(
      (transaction) =>
        fullPredicate == null || fullPredicate.applyTo(transaction),
    ),
    transactionsNav.sortColumn,
    transactionsNav.sortOrder,
    accounts,
    categories,
    tags,
  );

  // The lock button replaces all active non-ID filters with an ID-set filter
  // capturing the currently shown transactions. It's only useful when there
  // are non-ID filters to replace — if the only active filter is already the
  // ID filter (or nothing is filtered at all), there's nothing to lock. It's
  // also hidden when there are no visible transactions, since there'd be
  // nothing to lock to.
  const hasNonIdFilter =
    Object.entries(transactionsNav.columnSettings).some(
      ([col, settings]) =>
        (parseInt(col) as TransactionColumn) !== TransactionColumn.ID &&
        settings.predicate !== null,
    ) || transactionsNav.searchString.length > 0;

  const lockButton =
    reviewingIds !== null ||
    !hasNonIdFilter ||
    sortedFilteredTransactions.length === 0 ? null : (
      <RyeButton
        variant="outlined"
        vibe="subdued"
        size="sm"
        shape="rounded-rect"
        onClick={() => {
          const currentFilteredIds = sortedFilteredTransactions.map(
            (t) => t.id,
          );
          setViewingCount(currentFilteredIds.length);
          setTransactionsNav((prevState) => ({
            ...defaultTransactionsNav,
            sortColumn: prevState.sortColumn,
            sortOrder: prevState.sortOrder,
            columnSettings: {
              ...defaultTransactionsNav.columnSettings,
              [TransactionColumn.ID]: {
                ...defaultTransactionsNav.columnSettings[TransactionColumn.ID],
                predicate: buildIdFilterPredicate(currentFilteredIds),
              },
            },
          }));
        }}
        icon="lock_open"
        text={
          viewingCount !== null ? "Update locked transactions set" : "Lock view"
        }
        tooltip="Replace your active filters with just the currently shown transactions. They'll stay visible while you edit them, even if the edits would normally move them out of view."
      />
    );

  const searchBar = <TransactionsSearchBar />;

  const filterBadges = (
    <>
      {/* Column filter badges */}
      {Object.entries(transactionsNav.columnSettings).map(
        ([columnStr, settings]) => {
          // Convert string key to TransactionColumn enum value
          const columnEnum = parseInt(columnStr) as TransactionColumn;

          if (!settings.predicate) return null;
          if (columnEnum === TransactionColumn.ID) return null;

          const renderFilterDisplay = () => {
            switch (columnEnum) {
              case TransactionColumn.DATE:
                return (
                  <TransactionColumnFilterDisplayDate
                    predicate={settings.predicate!}
                  />
                );
              case TransactionColumn.ACCOUNT:
                return (
                  <TransactionColumnFilterDisplayAccount
                    predicate={settings.predicate!}
                  />
                );
              case TransactionColumn.TYPE:
                return (
                  <TransactionColumnFilterDisplayType
                    predicate={settings.predicate!}
                  />
                );
              case TransactionColumn.MERCHANT:
                return (
                  <TransactionColumnFilterDisplayMerchant
                    predicate={settings.predicate!}
                  />
                );
              case TransactionColumn.DESCRIPTION:
                return (
                  <TransactionColumnFilterDisplayDescription
                    predicate={settings.predicate!}
                  />
                );
              case TransactionColumn.CATEGORY:
                return (
                  <TransactionColumnFilterDisplayCategory
                    predicate={settings.predicate!}
                  />
                );
              case TransactionColumn.RULE:
                return (
                  <TransactionColumnFilterDisplayRule
                    predicate={settings.predicate!}
                  />
                );
              case TransactionColumn.REVIEWED:
                return (
                  <TransactionColumnFilterDisplayReviewed
                    predicate={settings.predicate!}
                  />
                );
              case TransactionColumn.AMOUNT:
                return (
                  <TransactionColumnFilterDisplayAmount
                    predicate={settings.predicate!}
                  />
                );
              case TransactionColumn.TAGS:
                return (
                  <TransactionColumnFilterDisplayTags
                    predicate={settings.predicate!}
                  />
                );
              default:
                return <span>Filter on unknown column</span>;
            }
          };

          return (
            <div key={columnStr} className="ml-2">
              <TransactionsScreenFilterBadge
                vibe={"primary"}
                onRemove={() => {
                  setTransactionsNav((prevState) => ({
                    ...prevState,
                    columnSettings: {
                      ...prevState.columnSettings,
                      [columnStr]: { ...settings, predicate: null },
                    },
                  }));
                }}
              >
                {renderFilterDisplay()}
              </TransactionsScreenFilterBadge>
            </div>
          );
        },
      )}
    </>
  );

  // Calculate number of unreviewed transactions
  const unreviewedTransactionsCount = Array.from(transactions.values()).filter(
    (transaction) => !transaction.reviewed,
  ).length;

  const hasActiveFilters =
    transactionsNav.searchString !== defaultTransactionsNav.searchString ||
    transactionsNav.searchStringExactMatch !==
      defaultTransactionsNav.searchStringExactMatch ||
    transactionsNav.sortColumn !== defaultTransactionsNav.sortColumn ||
    transactionsNav.sortOrder !== defaultTransactionsNav.sortOrder ||
    Object.entries(transactionsNav.columnSettings).some(
      ([column, settings]) =>
        settings.predicate !==
        defaultTransactionsNav.columnSettings[
          parseInt(column) as TransactionColumn
        ].predicate,
    );

  const reviewNewTransactionsButton =
    reviewingIds !== null ||
    hasActiveFilters ||
    unreviewedTransactionsCount === 0 ? null : (
      <RyeButton
        variant="outlined"
        vibe={"subdued"}
        size="sm"
        shape="rounded-rect"
        onClick={() => {
          // Lock to every unreviewed transaction in the full set (ignoring
          // whatever filters the user currently has active).
          const unreviewedTransactionIds = Array.from(transactions.values())
            .filter((transaction) => !transaction.reviewed)
            .map((transaction) => transaction.id);

          setReviewingIds(new Set(unreviewedTransactionIds));

          setTransactionsNav({
            ...defaultTransactionsNav,
            sortColumn: TransactionColumn.DATE,
            sortOrder: SortOrders.Ascending,
            columnSettings: {
              ...defaultTransactionsNav.columnSettings,
              [TransactionColumn.ID]: {
                ...defaultTransactionsNav.columnSettings[TransactionColumn.ID],
                predicate: buildIdFilterPredicate(unreviewedTransactionIds),
              },
            },
          });

          focusFirstTransactionRow();
        }}
        text={`Review ${unreviewedTransactionsCount} transactions`}
        icon="draft_orders"
        iconColor="yellow"
        tooltip="Filters for unreviewed transactions, sorts by date ascending, locks the transactions table against changes made to transactions, and focuses the first transaction."
      />
    );

  const resetToDefault = () => {
    setReviewingIds(null);
    setViewingCount(null);
    setTransactionsNav(defaultTransactionsNav);
  };

  let reviewingRemainingCount = 0;
  if (reviewingIds !== null) {
    reviewingIds.forEach((id) => {
      if (transactions.get(id)?.reviewed === false) reviewingRemainingCount++;
    });
  }

  const lockedViewIndicator =
    reviewingIds !== null ? (
      // Both indicators are shaped like the filter chips, as they stand in
      // for the filters while reviewing or viewing a locked set.
      <RyeBadge
        icon="draft_orders"
        text={`Reviewing ${reviewingIds.size} ${
          reviewingIds.size === 1 ? "transaction" : "transactions"
        }`}
        variant="outlined"
        vibe="primary"
        shape="fully-round"
        size="sm"
        className="border-transparent border-0"
      >
        <span className={buildClasses("text-sm")}>
          ({reviewingRemainingCount} remaining)
        </span>
      </RyeBadge>
    ) : viewingCount !== null ? (
      <RyeBadge
        icon="lock"
        text={`Viewing ${viewingCount} specific ${
          viewingCount === 1 ? "transaction" : "transactions"
        }`}
        variant="outlined"
        vibe="primary"
        shape="fully-round"
        size="sm"
        className="border-transparent border-0"
      />
    ) : null;

  const resetButton =
    reviewingIds !== null || viewingCount !== null ? (
      <RyeButton
        variant="filled"
        vibe="primary"
        size="sm"
        shape="rounded-rect"
        onClick={resetToDefault}
        icon="reset_settings"
        text="Reset view"
        tooltip="Exit this view, clear all filters, and reset sort order."
      />
    ) : hasActiveFilters ? (
      <RyeButton
        variant="filled"
        vibe="primary"
        size="sm"
        shape="rounded-rect"
        onClick={resetToDefault}
        icon="reset_settings"
        text="Reset view"
        tooltip="Clears all filters, clears search, resets sort order, and unlocks the transaction rows."
      />
    ) : null;

  return (
    <RyeScreenWrapper>
      <TransactionsAmountWidthContext.Provider value={amountWidth}>
        <RyeMainContentHeader title="Transactions">
          <div
            className={buildClasses(
              "flex",
              "items-center",
              "justify-between",
              "w-full",
            )}
          >
            <div className={buildClasses("flex", "items-center")}>
              {searchBar}
              {filterBadges}
            </div>
            <div className={buildClasses("flex", "items-center", "gap-2")}>
              {lockButton}
              {lockedViewIndicator}
              {resetButton}
              {reviewNewTransactionsButton}
            </div>
          </div>
        </RyeMainContentHeader>
        {transactions.size > 0 && <TransactionsColumnHeaders />}
        <RyeMainContentScrollShadow visible={scrolled} />
        <div
          className={buildClasses(
            "relative",
            "flex-grow",
            "flex-shrink",
            "flex",
            "flex-col",
            "min-h-0",
          )}
        >
          <RyeMainContentCard
            padTop={false}
            roundedTl={false}
            roundedTr={false}
            roundedBr={false}
            roundedBl={false}
          >
            <RyeScrollListener onScroll={onTableScroll} />
            {transactions.size === 0 ? (
              <RyeEmptyState
                icon="checklist_rtl"
                title="Your transactions will show up here"
                description="Link your financial accounts, and Bread will sync your transactions for you to categorize and review."
                actions={
                  <RyeButton
                    text="Link an account"
                    icon="add"
                    vibe="primary"
                    variant="filled"
                    onClick={() => {
                      // Like Balances: Settings' Accounts sits behind the
                      // dialog, ready for once the account is linked.
                      openSettings("accounts");
                      openModal(<AddNewAccountModal />);
                    }}
                  />
                }
              />
            ) : sortedFilteredTransactions.length === 0 ? (
              <RyeNoResults
                title="No transactions match"
                description="Nothing matches your search and filters."
                action={
                  <RyeButton
                    text="Reset view"
                    icon="reset_settings"
                    size="sm"
                    variant="outlined"
                    vibe="subdued"
                    onClick={resetToDefault}
                  />
                }
              />
            ) : (
              <TransactionsTable
                sortedFilteredTransactions={sortedFilteredTransactions}
                onSelectionCountChange={setSelectedTransactionCount}
                onSelectedIdsChange={handleSelectedIdsChange}
                deselectAllRef={deselectAllRef}
              />
            )}
          </RyeMainContentCard>
          <BulkEditBar
            selectedTransactionIds={selectedTransactionIds}
            deselectAll={() => deselectAllRef.current?.()}
          />
        </div>
        <RyeMainContentScrollShadow visible={moreBelow} edge="bottom" />
        <TransactionsFooter
          sortedFilteredTransactions={sortedFilteredTransactions}
          selectedTransactionCount={selectedTransactionCount}
          selectedTransactionIds={selectedTransactionIds}
        />
      </TransactionsAmountWidthContext.Provider>
    </RyeScreenWrapper>
  );
}

function applySortOrder(value: number, order: SortOrders) {
  if (order === SortOrders.Ascending) {
    return value;
  }
  return -value;
}

function getCompareFunction(
  col: TransactionColumn,
  order: SortOrders,
  accounts: Map<string, BreadAccount>,
  categories: Map<string, FirestoreDocCategory>,
  tags: Map<string, FirestoreDocTag>,
): (a: BreadTransaction, b: BreadTransaction) => number {
  const dateComparisonFunction = (a: BreadTransaction, b: BreadTransaction) => {
    return applySortOrder(
      dayjs(a.date).valueOf() - dayjs(b.date).valueOf(),
      order,
    );
  };

  if (col === TransactionColumn.ACCOUNT) {
    return (a: BreadTransaction, b: BreadTransaction) => {
      const accountNameA = accounts.get(a.account_id)?.name;
      const accountNameB = accounts.get(b.account_id)?.name;
      if (accountNameA === accountNameB) return dateComparisonFunction(a, b);
      if (!accountNameA) return applySortOrder(-1, order);
      if (!accountNameB) return applySortOrder(1, order);
      return applySortOrder(accountNameA.localeCompare(accountNameB), order);
    };
  }
  if (col === TransactionColumn.TYPE) {
    return (a: BreadTransaction, b: BreadTransaction) => {
      if (a.type === b.type) {
        if (a.subtype === b.subtype) return dateComparisonFunction(a, b);
        if (!a.subtype) return applySortOrder(-1, order);
        if (!b.subtype) return applySortOrder(1, order);
        return applySortOrder(a.subtype.localeCompare(b.subtype), order);
      }
      if (!a.type) return applySortOrder(-1, order);
      if (!b.type) return applySortOrder(1, order);
      return applySortOrder(a.type.localeCompare(b.type), order);
    };
  }
  if (col === TransactionColumn.MERCHANT) {
    return (a: BreadTransaction, b: BreadTransaction) => {
      if (a.merchant === b.merchant) return dateComparisonFunction(a, b);
      if (!a.merchant) return applySortOrder(-1, order);
      if (!b.merchant) return applySortOrder(1, order);
      return applySortOrder(
        a.merchant.name.localeCompare(b.merchant.name),
        order,
      );
    };
  }
  if (col === TransactionColumn.DESCRIPTION) {
    return (a: BreadTransaction, b: BreadTransaction) => {
      if (a.description === b.description) return dateComparisonFunction(a, b);
      if (!a.description) return applySortOrder(-1, order);
      if (!b.description) return applySortOrder(1, order);
      return applySortOrder(a.description.localeCompare(b.description), order);
    };
  }
  if (col === TransactionColumn.CATEGORY) {
    return (a: BreadTransaction, b: BreadTransaction) => {
      if (a.categoryId === b.categoryId) return dateComparisonFunction(a, b);
      if (!a.categoryId) return applySortOrder(-1, order);
      if (!b.categoryId) return applySortOrder(1, order);
      const categoryA = categories.get(a.categoryId)?.name;
      const categoryB = categories.get(b.categoryId)?.name;
      if (!categoryA) return applySortOrder(-1, order);
      if (!categoryB) return applySortOrder(1, order);
      return applySortOrder(categoryA.localeCompare(categoryB), order);
    };
  }
  if (col === TransactionColumn.TAGS) {
    const getSortedTagNames = (t: BreadTransaction): string[] =>
      t.tagIds
        .map((id) => tags.get(id))
        .filter((tag): tag is FirestoreDocTag => tag !== undefined)
        .sort((x, y) => x.order_position - y.order_position)
        .map((tag) => tag.name);
    return (a: BreadTransaction, b: BreadTransaction) => {
      const aNames = getSortedTagNames(a);
      const bNames = getSortedTagNames(b);
      if (aNames.length === 0 && bNames.length === 0)
        return dateComparisonFunction(a, b);
      if (aNames.length === 0) return applySortOrder(-1, order);
      if (bNames.length === 0) return applySortOrder(1, order);
      const minLen = Math.min(aNames.length, bNames.length);
      for (let i = 0; i < minLen; i++) {
        const cmp = aNames[i].localeCompare(bNames[i]);
        if (cmp !== 0) return applySortOrder(cmp, order);
      }
      if (aNames.length !== bNames.length) {
        return applySortOrder(aNames.length - bNames.length, order);
      }
      return dateComparisonFunction(a, b);
    };
  }
  if (col === TransactionColumn.AMOUNT) {
    return (a: BreadTransaction, b: BreadTransaction) => {
      if (a.amount === b.amount) return dateComparisonFunction(a, b);
      return applySortOrder(a.amount - b.amount, order);
    };
  }

  return dateComparisonFunction;
}

// Builds a predicate that matches transactions whose `id` is in the given set,
// using the same Any(Equals(Field, Literal), ...) shape the category/account
// filters use. Returns null for an empty id list (no lock to apply).
function buildIdFilterPredicate(ids: string[]): Predicate | null {
  if (ids.length === 0) return null;
  return P.Any(
    ...ids.map((id) =>
      P.Equals(P.Field<BreadTransaction, "id">("id"), P.Literal(id)),
    ),
  );
}

function sortTransactions(
  transactions: BreadTransaction[],
  col: TransactionColumn,
  order: SortOrders,
  accounts: Map<string, BreadAccount>,
  categories: Map<string, FirestoreDocCategory>,
  tags: Map<string, FirestoreDocTag>,
): BreadTransaction[] {
  return transactions.sort(
    getCompareFunction(col, order, accounts, categories, tags),
  );
}

/** A loading row, roughly shaped like a transaction's columns. */
function TransactionSkeletonRow(): JSX.Element {
  return (
    <div
      className={buildClasses(
        "h-10",
        "flex",
        "items-center",
        "gap-4",
        "px-2",
        "border-t-tiny",
        "border-t-surface-600",
        "first:border-t-0",
      )}
    >
      <RyeSkeleton
        shape="circle"
        className={buildClasses("size-4", "shrink-0")}
      />
      <RyeSkeleton className={buildClasses("h-3", "w-14", "shrink-0")} />
      <RyeSkeleton className={buildClasses("h-3", "w-[18%]")} />
      <RyeSkeleton className={buildClasses("h-3", "w-[30%]")} />
      <RyeSkeleton className={buildClasses("h-5", "w-28", "rounded-full")} />
      <RyeSkeleton
        className={buildClasses("h-3", "w-16", "ml-auto", "shrink-0")}
      />
    </div>
  );
}
